import React, { useEffect, useState } from "react";
import { ChatMessage } from "../types";
import { API_ENDPOINTS } from "../../../core/constants/endpoints";
import { apiService } from "../../../core/api";
import styles from "./MessageBubble.module.scss";

const AuthenticatedImage: React.FC<{
  url: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}> = ({ url, alt, className, onClick }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    apiService
      .getBlob(url)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    />
  );
};

/** Fetch a file with auth and open it in a new browser tab */
const handleAuthOpen = async (url: string, mime: string) => {
  try {
    const blob = await apiService.getBlob(url);
    const typedBlob = new Blob([blob], { type: mime });
    const objectUrl = URL.createObjectURL(typedBlob);
    window.open(objectUrl, "_blank");
    // Revoke after a short delay so the new tab has time to load
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    console.error("Failed to open file:", err);
  }
};

/** Fetch a file with auth and trigger a download */
const handleAuthDownload = async (url: string, filename: string) => {
  try {
    const blob = await apiService.getBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error("Download failed:", err);
  }
};

interface MessageBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  avatarUrl?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIcon = (mime: string): string => {
  if (mime.startsWith("image/")) return "pi pi-image";
  if (mime === "application/pdf") return "pi pi-file-pdf";
  if (mime.startsWith("video/")) return "pi pi-video";
  if (mime.startsWith("audio/")) return "pi pi-volume-up";
  return "pi pi-file";
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  showAvatar = false,
  avatarUrl,
}) => {
  const formatTime = (timestamp: string): string => {
    try {
      const parts = timestamp.split(" ");
      if (parts.length !== 2) return timestamp;

      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split("-");
      const [hours, minutes] = timePart.split(":");

      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
      );

      const now = new Date();
      const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (isToday) return time;
      return (
        date.toLocaleDateString([], {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) +
        " " +
        time
      );
    } catch {
      return timestamp;
    }
  };

  const att = message.attachment;
  const isImage = att?.mime.startsWith("image/");
  // Hide text when it's just the filename echoed back as the message
  const showText = !att || message.message !== att.filename;

  return (
    <div
      className={`${styles.messageBubble} ${
        isOwnMessage ? styles.ownMessage : styles.otherMessage
      }`}
    >
      {showAvatar && !isOwnMessage && (
        <div className={styles.avatar}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className={styles.avatarImage} />
          ) : (
            <div className={styles.avatarPlaceholder}>
              <i className="pi pi-user" />
            </div>
          )}
        </div>
      )}

      <div className={styles.bubbleContent}>
        {/* Image attachments — preview inline, click opens in new tab with auth */}
        {att && isImage && (
          <div className={styles.imageWrap}>
            <AuthenticatedImage
              url={API_ENDPOINTS.CHAT.MESSAGE_FILE(att.fid)}
              alt={att.filename}
              className={styles.attachmentImage}
              onClick={() =>
                handleAuthOpen(
                  API_ENDPOINTS.CHAT.MESSAGE_FILE(att.fid),
                  att.mime,
                )
              }
            />
          </div>
        )}

        {/* Non-image attachments — download with auth on click */}
        {att && !isImage && (
          <button
            type="button"
            onClick={() =>
              handleAuthDownload(
                API_ENDPOINTS.CHAT.MESSAGE_FILE(att.fid),
                att.filename,
              )
            }
            className={styles.fileChip}
          >
            <i className={fileIcon(att.mime)} />
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{att.filename}</span>
              <span className={styles.fileSize}>
                {formatFileSize(att.filesize)}
              </span>
            </div>
            <i className="pi pi-download" />
          </button>
        )}

        {showText && (
          <div className={styles.messageText}>{message.message}</div>
        )}

        <div className={styles.messageInfo}>
          <span className={styles.timestamp}>
            {formatTime(message.created)}
          </span>
          {isOwnMessage && message.is_read && (
            <i className="pi pi-check" title="Read" />
          )}
        </div>
      </div>
    </div>
  );
};
